/**
 * Build a bidirectional `seq → paired seq + latency` index by walking the
 * messages in arrival order and matching responses/errors to their preceding
 * requests via `rpcId`. Both sides of a pair are written, so `pairs.get(req.seq)`
 * and `pairs.get(resp.seq)` both return the same `latencyMs`.
 *
 * Sole authoritative pair index — CLI (`stats`, `inspect`, `validate`) and
 * UI (`StatsBar`, `DetailPanel`, `PerformancePanel`) all read latencies
 * through this function so percentiles agree to the millisecond.
 */
export function buildPairIndex(messages) {
    const idToReq = new Map();
    const out = new Map();
    for (const m of messages) {
        if (m.rpcId === undefined || m.rpcId === null)
            continue;
        const key = String(m.rpcId);
        if (m.kind === 'request') {
            idToReq.set(key, m);
        }
        else if (m.kind === 'response' || m.kind === 'error') {
            const req = idToReq.get(key);
            if (!req)
                continue;
            const latencyMs = Math.max(0, m.timestamp - req.timestamp);
            out.set(req.seq, { pairSeq: m.seq, latencyMs });
            out.set(m.seq, { pairSeq: req.seq, latencyMs });
        }
    }
    return out;
}
/**
 * Per-method counts + latency percentiles. Latency is collected from REQUEST
 * rows (not responses) so the same pair is counted once. Notifications contribute
 * counts only — no latency by definition.
 *
 * Sort order: highest count first; ties broken by request-kind before notification.
 */
export function buildPerMethodStats(messages) {
    const pairs = buildPairIndex(messages);
    const reqByMethod = new Map();
    const ntfByMethod = new Map();
    for (const m of messages) {
        if (!m.method)
            continue;
        if (m.kind === 'request') {
            let b = reqByMethod.get(m.method);
            if (!b) {
                b = { count: 0, latencies: [] };
                reqByMethod.set(m.method, b);
            }
            b.count += 1;
            const p = pairs.get(m.seq);
            if (p)
                b.latencies.push(p.latencyMs);
        }
        else if (m.kind === 'notification') {
            let b = ntfByMethod.get(m.method);
            if (!b) {
                b = { count: 0, latencies: [] };
                ntfByMethod.set(m.method, b);
            }
            b.count += 1;
        }
    }
    const out = [];
    for (const [method, bucket] of reqByMethod) {
        bucket.latencies.sort((a, b) => a - b);
        out.push({
            method,
            kind: 'request',
            count: bucket.count,
            sampleSize: bucket.latencies.length,
            p50: bucket.latencies.length ? percentile(bucket.latencies, 50) : null,
            p99: bucket.latencies.length ? percentile(bucket.latencies, 99) : null,
            max: bucket.latencies.length ? bucket.latencies[bucket.latencies.length - 1] : null,
            totalLatencyMs: bucket.latencies.length
                ? bucket.latencies.reduce((acc, x) => acc + x, 0)
                : null,
            latencies: bucket.latencies,
        });
    }
    for (const [method, bucket] of ntfByMethod) {
        out.push({
            method,
            kind: 'notification',
            count: bucket.count,
            sampleSize: 0,
            p50: null,
            p99: null,
            max: null,
            totalLatencyMs: null,
            latencies: [],
        });
    }
    out.sort((a, b) => b.count - a.count || (a.kind === 'request' ? -1 : 1));
    return out;
}
/**
 * Linear-interpolation percentile. Returns raw float — callers round at the
 * presentation layer if needed. The same algorithm sits in
 * `packages/ui/src/lib/format.ts` (UI StatsBar) — keep them in lock-step so
 * the inspector and `acp-devtools stats` agree to the millisecond.
 */
export function percentile(sortedAsc, p) {
    if (sortedAsc.length === 0)
        return 0;
    const rank = (p / 100) * (sortedAsc.length - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    const a = sortedAsc[lo];
    const b = sortedAsc[hi];
    if (a === undefined || b === undefined)
        return a ?? 0;
    if (lo === hi)
        return a;
    return a + (b - a) * (rank - lo);
}
export function mean(values) {
    if (values.length === 0)
        return 0;
    return values.reduce((acc, x) => acc + x, 0) / values.length;
}
//# sourceMappingURL=method-stats.js.map